import * as React from "react";
import { $, merge, media } from "glamor";
import { theme } from "styles/theme";
import { Tabs, Tab } from "material-ui/Tabs";
import CartIcon from "components/icons/assortments/cart";
import ReviewIcon from "components/icons/assortments/review";
import ShopIcon from "components/icons/assortments/add-hang-tag";
import classNames from "classnames";
import { View } from "constants/product-collection";
import { CatalogActions } from "./product-collection-side-panel/product-collection-shop-catalog-search-actions";

enum TabValues {
    Plan = "plan",
    Shop = "shop",
    Assortment = "assortment",
    Review = "review",
}

interface IProps {
    router: any;
    location: any;
    sectionToolbarContentRef: React.RefObject<HTMLDivElement>;
    onAddBulkItems?: any;
    entries?: any;
    editable: boolean;
    bulkAddingItems: boolean;
    isRollupMode: boolean;
    canShopBrand?: boolean;
    sizingAllocationEnabled: boolean;
    allowDuplicateProduct: boolean;
    onResetToDefaultShopView: () => void;
    onConfigureShopView: () => void;
    selectedShopViewKey: string;
}

export const ProductCollectionAppBar: React.FunctionComponent<IProps> = ({
    location,
    router,
    sectionToolbarContentRef,
    onAddBulkItems,
    entries,
    editable,
    bulkAddingItems,
    isRollupMode,
    canShopBrand,
    sizingAllocationEnabled,
    onResetToDefaultShopView,
    onConfigureShopView,
    selectedShopViewKey,
}) => {
    const tabWidth = React.useCallback(
        (baseWidth: number) => {
            let width = baseWidth;
            if (isRollupMode && !sizingAllocationEnabled) {
                width = baseWidth / 2;
            }
            return `${width}px`;
        },
        [isRollupMode, sizingAllocationEnabled]
    );

    const styles = React.useMemo(
        () =>
            merge(
                {
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    height: "48px",
                    flexShrink: 0,
                    flexGrow: 0,
                    padding: "0",
                    paddingLeft: "24px",
                    // borderBottom: `1px solid ${theme.palette.borderColor}`,
                },
                $(" .assortmentNav", {
                    width: `${tabWidth(336)}`,
                    flexShrink: 0,
                    flexGrow: 0,
                }),
                $(" .assortmentNav > div", {
                    backgroundColor: "transparent !important",
                }),
                $(" .assortmentNav button > div > div, .assortmentNav button.disabled > div", {
                    height: "48px !important",
                    color: `${theme.palette.darkGrey} !important`,
                    textTransform: "none !important",
                    flexDirection: "row !important",
                    // justifyContent: "left !important",
                    fontWeight: "bold",
                    fontSize: "12px !important",
                }),
                $(" .assortmentNav button > div > div svg, .assortmentNav button.disabled > div svg", {
                    color: `${theme.palette.darkGrey} !important`,
                    margin: "0 8px 0 0 !important",
                    height: "16px !important",
                    width: "16px !important",
                }),
                $(" .assortmentNav button.disabled > div", {
                    color: `${theme.palette.grey} !important`,
                }),
                $(" .assortmentNav button.disabled > div svg", {
                    color: `${theme.palette.grey} !important`,
                }),
                $(" .assortmentNav button.active > div > div", {
                    color: `${theme.palette.darkestGrey} !important`,
                }),
                $(" .assortmentNav button.active > div > div svg", {
                    color: `${theme.palette.accent1Color} !important`,
                }),
                $(" .separator", {
                    width: "36px",
                    height: "100%",
                }),
                $(" .sectionToolbarContent", {
                    flexShrink: 1,
                    flexGrow: 1,
                    height: "100%",
                    // shrink toolbar
                }),
                $(" .assortmentNav button.plan > div > div", {
                    color: `${theme.palette.grey} !important`,
                }),
                $(" .assortmentNav button.plan > div > div svg", {
                    color: `${theme.palette.grey} !important`,
                }),
                media(
                    "(max-width: 640px)",
                    $(" .assortmentNav", {
                        width: `${tabWidth(291)}`,
                        flexShrink: 0,
                        flexGrow: 0,
                    })
                ),
                media(
                    "(max-width: 600px)",
                    {
                        paddingLeft: 0,
                    },
                    $(" .tabLabel", {
                        display: "none",
                    }),
                    $(" .assortmentNav", {
                        width: `${tabWidth(120)}`,
                        flexShrink: 0,
                        flexGrow: 0,
                    }),
                    $(" .separator", {
                        width: "18px",
                    })
                )
            ),
        [tabWidth]
    );

    const tab = React.useMemo(() => {
        if (location.query.mode === TabValues.Plan) {
            return TabValues.Plan;
        } else if (location.query.mode === TabValues.Review) {
            return TabValues.Review;
        } else if (location.query.mode) {
            return TabValues.Assortment;
        }
        return TabValues.Shop;
    }, [location]);

    const tabs = React.useMemo(() => {
        return [
            !isRollupMode
                ? {
                      key: TabValues.Shop,
                      label: "Shop",
                      icon: <ShopIcon />,
                      disabled: !canShopBrand,
                  }
                : null,
            {
                key: TabValues.Assortment,
                label: "Assortment",
                icon: <CartIcon />,
            },
            sizingAllocationEnabled
                ? {
                      key: TabValues.Review,
                      label: "Review",
                      icon: <ReviewIcon />,
                  }
                : null,
        ]
            .filter((tab) => !!tab)
            .map(({ key, label, icon, disabled = false }) => {
                return (
                    <Tab
                        data-test={`${key}-tab`}
                        key={key}
                        icon={icon}
                        label={<span className="tabLabel">{label}</span>}
                        value={key}
                        className={classNames(key, { active: key === tab, disabled })}
                        disabled={disabled}
                    />
                );
            });
    }, [tab, isRollupMode, canShopBrand, sizingAllocationEnabled]);

    const handleTabChange = React.useCallback(
        (value) => {
            switch (value) {
                case TabValues.Shop:
                    const shopQuery = { ...location.query };
                    delete shopQuery.mode;
                    router.push({
                        pathname: location.pathname,
                        query: shopQuery,
                    });
                    break;
                case TabValues.Assortment:
                    router.push({
                        pathname: location.pathname,
                        query: {
                            ...location.query,
                            mode: "cart",
                        },
                    });
                    break;
                case TabValues.Plan:
                    router.push({
                        pathname: location.pathname,
                        query: {
                            ...location.query,
                            mode: TabValues.Plan,
                        },
                    });
                    break;
                case TabValues.Review:
                    router.push({
                        pathname: location.pathname,
                        query: {
                            ...location.query,
                            mode: TabValues.Review,
                        },
                    });
                    break;

                default:
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location]
    );

    const bulkAddEnabled = entries.results && entries.results.length;
    const { catalog } = router.params;
    const { query, pathname } = location;
    const isCatalogPage = catalog && !query.mode;
    const filters = JSON.parse(query.filters || "{}");
    // eslint-disable-next-line dot-notation
    const isGlobalSearchPage = (pathname.match("/shop/search") || filters["__search"]) && !query.mode;

    return (
        <div {...styles}>
            {![View.History, View.Dataloss].includes(query.cartView) && (
                <Tabs className="assortmentNav" value={tab} onChange={handleTabChange}>
                    {tabs}
                </Tabs>
            )}
            {![View.History, View.Dataloss].includes(query.cartView) &&
                editable &&
                (isCatalogPage || isGlobalSearchPage) && (
                    <div style={{ position: "absolute", right: "10px" }}>
                        <CatalogActions
                            onAddAllItems={onAddBulkItems}
                            disabled={!bulkAddEnabled}
                            bulkAddingItems={bulkAddingItems}
                            onResetToDefaultShopView={onResetToDefaultShopView}
                            onConfigureShopView={onConfigureShopView}
                            selectedShopViewKey={selectedShopViewKey}
                            isCatalogPage={isCatalogPage}
                        />
                    </div>
                )}
            <div className="separator" />
            <div ref={sectionToolbarContentRef} className="sectionToolbarContent" />
        </div>
    );
};
